import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import RedoIcon from '@mui/icons-material/Redo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TranslateIcon from '@mui/icons-material/Translate';
import UndoIcon from '@mui/icons-material/Undo';
import { ControlPanel } from './components/ControlPanel';
import type { ExportSettings } from './components/ExportControls';
import { ShortcutsDialog } from './components/ShortcutsDialog';
import { ThumbnailCanvas } from './components/ThumbnailCanvas';
import { EXPORT_SIZES, STORAGE_KEYS } from './constants';
import { detectLanguage, dictionaries } from './i18n';
import {
  buildFileName,
  copyBlobToClipboard,
  downloadBlob,
  exportThumbnail,
} from './lib/export';
import { loadJSON, saveJSON } from './lib/storage';
import { useEditor } from './state/useEditor';
import { useImage } from './state/useImage';
import { presetToPatch, usePresets } from './state/usePresets';
import type { Language, LayerId, StylePreset } from './types';

type Toast = { message: string; severity: 'success' | 'error' | 'info' } | null;

const DEFAULT_EXPORT: ExportSettings = { sizeIndex: 0, format: 'png', quality: 92 };

export default function App() {
  const editor = useEditor();
  const { state } = editor;
  const image = useImage();
  const presets = usePresets();

  const [language, setLanguage] = useState<Language>(
    () => loadJSON<Language>(STORAGE_KEYS.language) ?? detectLanguage(),
  );
  const t = dictionaries[language];

  const [selection, setSelection] = useState<LayerId | null>(null);
  const [expanded, setExpanded] = useState<string | false>('main');
  const [toast, setToast] = useState<Toast>(null);
  const [exporting, setExporting] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [exportSettings, setExportSettings] = useState<ExportSettings>(
    () => ({ ...DEFAULT_EXPORT, ...loadJSON<Partial<ExportSettings>>(STORAGE_KEYS.exportOptions) }),
  );

  useEffect(() => saveJSON(STORAGE_KEYS.language, language), [language]);
  useEffect(() => saveJSON(STORAGE_KEYS.exportOptions, exportSettings), [exportSettings]);

  useEffect(() => {
    if (editor.restoredFromStorage) setToast({ message: t.restored, severity: 'info' });
    // Only announced once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!image.error) return;
    const message =
      image.error === 'tooLarge'
        ? t.imageTooLarge
        : image.error === 'invalid'
          ? t.imageInvalid
          : t.imageLoadFailed;
    setToast({ message, severity: 'error' });
    image.dismissError();
  }, [image, t]);

  const handleFile = useCallback(
    (file: File) => {
      void image.load(file);
    },
    [image],
  );

  const runExport = useCallback(
    async (mode: 'download' | 'copy') => {
      if (!image.image) {
        setToast({ message: t.needImage, severity: 'info' });
        return;
      }
      const sizes = EXPORT_SIZES[state.ratio];
      const size = sizes[Math.min(exportSettings.sizeIndex, sizes.length - 1)]!;
      setExporting(true);
      try {
        const blob = await exportThumbnail({
          state,
          image: image.image.source,
          imageWidth: image.image.width,
          imageHeight: image.image.height,
          width: size.width,
          height: size.height,
          format: exportSettings.format,
          quality: exportSettings.quality,
        });
        if (mode === 'download') {
          downloadBlob(blob, buildFileName(state, blob.type));
          setToast({ message: t.downloaded, severity: 'success' });
        } else {
          await copyBlobToClipboard(blob);
          setToast({ message: t.copied, severity: 'success' });
        }
      } catch {
        setToast({
          message: mode === 'copy' ? t.copyFailed : t.exportFailed,
          severity: 'error',
        });
      } finally {
        setExporting(false);
      }
    },
    [image.image, state, exportSettings, t],
  );

  // Keep the latest export handler reachable from the keydown listener without
  // re-binding the listener on every state change.
  const exportRef = useRef(runExport);
  exportRef.current = runExport;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      if (key === 'z') {
        event.preventDefault();
        if (event.shiftKey) editor.redo();
        else editor.undo();
      } else if (key === 'y') {
        event.preventDefault();
        editor.redo();
      } else if (key === 's') {
        event.preventDefault();
        void exportRef.current('download');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editor]);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith('image/'),
      );
      if (file) {
        event.preventDefault();
        void image.load(file);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [image]);

  const handleMove = useCallback(
    (id: LayerId, dx: number, dy: number) => {
      editor.setLayer(
        id,
        { x: Math.round(state[id].x + dx), y: Math.round(state[id].y + dy) },
        { tag: `move:${id}` },
      );
    },
    [editor, state],
  );

  const handleApplyPreset = useCallback(
    (preset: StylePreset) => {
      editor.applyPatch(presetToPatch(preset, state));
      setToast({ message: t.presetApplied, severity: 'success' });
    },
    [editor, state, t],
  );

  const handleSavePreset = useCallback(
    (name: string) => {
      presets.save(name, state);
      setToast({ message: t.presetSaved, severity: 'success' });
    },
    [presets, state, t],
  );

  const handleReset = useCallback(() => {
    if (!window.confirm(t.resetConfirm)) return;
    editor.reset();
    image.clear();
    setSelection(null);
  }, [editor, image, t]);

  const selectedLabel = useMemo(
    () => (selection === 'main' ? t.mainTitle : selection === 'sub' ? t.subTitle : null),
    [selection, t],
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mb: 3,
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h1" component="h1">
            {t.appTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '62ch' }}>
            {t.appSubtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title={`${t.undo} (Ctrl+Z)`}>
            <span>
              <IconButton onClick={editor.undo} disabled={!editor.canUndo} aria-label={t.undo}>
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`${t.redo} (Ctrl+Shift+Z)`}>
            <span>
              <IconButton onClick={editor.redo} disabled={!editor.canRedo} aria-label={t.redo}>
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t.reset}>
            <IconButton onClick={handleReset} aria-label={t.reset}>
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.shortcuts}>
            <IconButton onClick={() => setShortcutsOpen(true)} aria-label={t.shortcuts}>
              <KeyboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.toggleLanguage}>
            <IconButton
              onClick={() => setLanguage((current) => (current === 'ko' ? 'en' : 'ko'))}
              aria-label={t.toggleLanguage}
            >
              <TranslateIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(340px, 420px) 1fr' },
        }}
      >
        {/* Preview first on small screens so edits stay visible while scrolling. */}
        <Paper
          elevation={0}
          sx={{
            order: { xs: -1, lg: 1 },
            p: { xs: 1.5, md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            position: { lg: 'sticky' },
            top: { lg: 24 },
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t.preview}
              </Typography>
              {selectedLabel && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="caption" color="primary.main">
                    {t.selectedLayer}: {selectedLabel}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => selection && editor.setLayer(selection, { x: 0, y: 0 })}
                  >
                    {t.centerLayer}
                  </Button>
                  <Button size="small" color="inherit" onClick={() => setSelection(null)}>
                    {t.selectNone}
                  </Button>
                </Stack>
              )}
            </Stack>
            <ThumbnailCanvas
              state={state}
              image={image.image}
              selection={selection}
              onSelect={setSelection}
              onMove={handleMove}
              emptyText={t.previewEmpty}
              ariaLabel={t.previewAria}
            />
            <Typography variant="caption" color="text.secondary">
              {t.dragHint}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' }}
        >
          <ControlPanel
            t={t}
            language={language}
            editor={editor}
            image={image.image}
            imageLoading={image.isLoading}
            onFile={handleFile}
            onClearImage={image.clear}
            presets={presets.presets}
            onApplyPreset={handleApplyPreset}
            onSavePreset={handleSavePreset}
            onDeletePreset={presets.remove}
            exportSettings={exportSettings}
            onExportSettings={(patch) => setExportSettings((prev) => ({ ...prev, ...patch }))}
            onDownload={() => void runExport('download')}
            onCopy={() => void runExport('copy')}
            exporting={exporting}
            expanded={expanded}
            onExpanded={setExpanded}
            onFocusLayer={setSelection}
          />
        </Paper>
      </Box>

      <Divider sx={{ mt: 5, mb: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        {t.copyright}
      </Typography>

      <ShortcutsDialog t={t} open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
}
